import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import Modal from "../components/Modal";
import { PrimaryOutlinedCloseCTAButton } from "../../common/Buttons";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useSnackbar } from "notistack";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the file extension from a URL, ignoring query-string parameters.
 * e.g. "https://bucket.s3.amazonaws.com/receipt.pdf?X-Amz..." → "pdf"
 */
const getExtension = (url) => {
  if (!url) return "";
  return url.split("?")[0].split(".").pop().toLowerCase().trim();
};

/**
 * Returns the filename component from a URL, ignoring query-string parameters.
 * e.g. "https://bucket.s3.amazonaws.com/receipt.pdf?..." → "receipt.pdf"
 */
const getFileName = (url) => {
  if (!url) return "Document";
  return url.split("?")[0].split("/").pop() || "Document";
};

/**
 * Detects whether the URL is a raw private S3 object URL.
 *
 * AWSService.uploadToS3 resolves to url.split("?")[0], which strips the
 * presigned PUT query parameters and leaves a private object path that will
 * return AccessDenied if accessed directly.
 *
 * Requirement 8: The backend must return an authorized URL (presigned GET,
 * CDN, or authenticated proxy) in GET /getBookingsDet before preview works.
 */
const isRawPrivateS3Url = (url) => {
  if (!url) return false;
  const looksLikeS3 =
    url.includes(".amazonaws.com") || url.includes(".s3.");
  const hasPresignedGetParams =
    url.includes("X-Amz-Signature") || url.includes("AWSAccessKeyId");
  return looksLikeS3 && !hasPresignedGetParams;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CancellationAttachmentPreview = ({ booking, onClose }) => {
  const [loadError, setLoadError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  if (!booking) return null;

  const storedUrl =
    booking.cancelFileurl ||
    booking.bookingRoomInvoice?.find((invoice) => invoice.cancelFileurl)
      ?.cancelFileurl;
  // GET /getBookingsDet returns a presigned GET URL for the private S3 object.
  const docUrl = booking.cancelFileSignedUrl || storedUrl;
  const fileName = decodeURIComponent(getFileName(storedUrl));
  const ext = getExtension(storedUrl);
  const isImage = ["jpg", "jpeg", "png"].includes(ext);
  const isPdf = ext === "pdf";
  const urlIsPrivate = isRawPrivateS3Url(docUrl);

  /**
   * Downloads the document without navigating the current tab.
   *
   * The HTML `download` attribute is silently ignored by browsers when the
   * href is a cross-origin URL (e.g. S3). The browser treats the click as a
   * normal navigation and replaces the current tab.
   *
   * Fix: fetch the file as a Blob → create a same-origin blob: URL →
   * trigger a programmatic anchor click. The `download` attribute always
   * works on blob: URLs because they are same-origin. The object URL is
   * revoked immediately after the click to free memory.
   *
   * If the S3 bucket does not return CORS headers that permit the fetch
   * (Access-Control-Allow-Origin), the fetch will reject. In that case we
   * report the limitation via the existing snackbar and do NOT navigate.
   */
  const handleDownload = async () => {
    if (!docUrl) return;
    try {
      // Keep the query string — it carries the presigned GET signature.
      const response = await fetch(docUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      // CORS blocks the blob fetch — open the signed URL in a new tab instead
      // so the current tab is not navigated away.
      if (!urlIsPrivate) {
        window.open(docUrl, "_blank", "noopener,noreferrer");
        return;
      }
      enqueueSnackbar(
        "Download failed: the document server does not permit direct downloads.",
        { variant: "error" }
      );
    }
  };

  const renderContent = () => {
    if (!docUrl) {
      return (
        <Notice>
          No supporting document is associated with this cancellation.
        </Notice>
      );
    }

    // Requirement 8: Do not assume a raw S3 URL is publicly accessible.
    // Show a clear backend dependency notice instead of opening a URL
    // that would return AccessDenied.
    if (urlIsPrivate) {
      return (
        <DependencyNotice>
          <DependencyIcon>
            <span role="img" aria-label="warning">⚠️</span>
          </DependencyIcon>
          <DependencyText>
            <strong>Preview unavailable — backend dependency</strong>
            <p>
              The supporting document is stored privately in S3. Inline preview
              requires the backend to return an authorized URL (presigned GET URL
              or authenticated proxy endpoint) inside the{" "}
              <code>GET /getBookingsDet</code> response.
            </p>
            <FileMeta>
              Stored file: <code>{fileName}</code>
            </FileMeta>
            <FileMeta>
              See the <em>Backend Developer Requirements</em> section for the
              exact API specification.
            </FileMeta>
          </DependencyText>
        </DependencyNotice>
      );
    }

    if (loadError) {
      return (
        <DependencyNotice>
          <DependencyIcon>
            <span role="img" aria-label="warning">⚠️</span>
          </DependencyIcon>
          <DependencyText>
            <strong>Unable to load document</strong>
            <p>
              The URL may have expired or access may be restricted. Please
              ensure the backend returns a valid authorized URL for this
              attachment.
            </p>
          </DependencyText>
        </DependencyNotice>
      );
    }

    if (isImage) {
      return (
        <PreviewImg
          src={docUrl}
          alt={fileName}
          onError={() => setLoadError(true)}
        />
      );
    }

    if (isPdf) {
      return (
        <PreviewFrame
          src={docUrl}
          title={fileName}
          onError={() => setLoadError(true)}
        />
      );
    }

    return <Notice>Unsupported file type for inline preview.</Notice>;
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      width={740}
      hideClose={true}
    >
      <ModalBody>
        {/* Custom header: title/subtitle on left, download + close on right */}
        <ModalHeader>
          <ModalHeaderText>
            <ModalTitle>Supporting Document</ModalTitle>
            <ModalSubtitle>{fileName}</ModalSubtitle>
          </ModalHeaderText>
          <ModalHeaderActions>
            {docUrl && (
              <HeaderIconBtn
                title="Download Document"
                aria-label="Download Document"
                onClick={handleDownload}
              >
                <GetAppIcon style={{ fontSize: 18 }} />
              </HeaderIconBtn>
            )}
            <HeaderIconBtn
              title="Close"
              aria-label="Close"
              $isClose
              onClick={onClose}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </HeaderIconBtn>
          </ModalHeaderActions>
        </ModalHeader>
        <PreviewArea>{renderContent()}</PreviewArea>
        <Actions>
          <PrimaryOutlinedCloseCTAButton
            style={{ width: "110px", padding: "10px 12px", borderRadius: 8 }}
            onClick={onClose}
          >
            Close
          </PrimaryOutlinedCloseCTAButton>
        </Actions>
      </ModalBody>
    </Modal>
  );
};

export default CancellationAttachmentPreview;


// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const ModalBody = styled.div`
  padding: 0 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PreviewArea = styled.div`
  min-height: 120px;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid ${COLORS.BORDER_GREY};
  margin: 0 auto;
  display: block;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 65vh;
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 8px;
`;

const DependencyNotice = styled.div`
  display: flex;
  gap: 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 18px;
  flex: 1;
`;

const DependencyIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const DependencyText = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: #78350f;
  line-height: 1.6;

  strong {
    font-family: ${theme.fonts.primaryFontSemiBold};
    display: block;
    margin-bottom: 6px;
    color: #92400e;
  }

  p {
    margin: 0 0 8px;
  }

  code {
    font-family: monospace;
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 12px;
  }

  em {
    font-style: italic;
  }
`;

const FileMeta = styled.p`
  margin: 0 0 4px !important;
  font-size: 12px !important;
  color: #a16207 !important;
`;

const Notice = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 14px;
  color: #64748b;
  padding: 24px;
  text-align: center;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/* Custom modal header — title + download + close in one row */
const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 0 0;
  border-bottom: 1px solid ${COLORS.BORDER_GREY};
  padding-bottom: 16px;
  flex-shrink: 0;
`;

const ModalHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin: 0;
`;

const ModalSubtitle = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #5c6b7f;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

/**
 * Shared icon button for Download and Close in the modal header.
 * $isClose — if true, shows red hover (matching the app's existing CloseBtn).
 */
const HeaderIconBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;
  &:hover {
    background: ${({ $isClose }) => ($isClose ? "#fee2e2" : "#e0f2fe")};
    color: ${({ $isClose }) => ($isClose ? "#ef4444" : "#0284c7")};
  }
`;
